import { useState, useEffect, useMemo } from "preact/hooks";
import { detectIncognito } from "detectincognitojs";

// ============================================================================
// XR CHECK STATUS
// ============================================================================
export enum XRCheckStatus {
  CHECKING = "CHECKING",
  OK = "OK",
  NO_TARGET_URL = "NO_TARGET_URL",
  IOS_NOT_SAFARI = "IOS_NOT_SAFARI",
  IOS_NO_APP_CLIP = "IOS_NO_APP_CLIP",
  IOS_INCOGNITO = "IOS_INCOGNITO",
  SHOW_APP_CLIP = "SHOW_APP_CLIP",
  ANDROID_NOT_CHROME = "ANDROID_NOT_CHROME",
  DESKTOP = "DESKTOP",
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
function isIOS(): boolean {
  return (
    /^iP/.test(navigator.platform) ||
    (/^Mac/.test(navigator.platform) && navigator.maxTouchPoints > 4)
  );
}

function isMobileDevice(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
}

function getIOSVersion(): number | null {
  const extract = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
  if (extract && extract.length >= 3) {
    const [, major, minor] = extract;
    return parseFloat(`${major}.${minor}`);
  }
  return null;
}

// ============================================================================
// ERROR/INFO MESSAGES
// ============================================================================
export const XR_STATUS_MESSAGES: Record<string, string> = {
  [XRCheckStatus.IOS_NOT_SAFARI]: "Open this link in Safari to use AR.",
  [XRCheckStatus.IOS_INCOGNITO]: "Disable private browsing mode in Safari to use AR.",
  [XRCheckStatus.IOS_NO_APP_CLIP]: "Your device requires iOS 15.6 or later for AR.",
  [XRCheckStatus.ANDROID_NOT_CHROME]: "Open this link in Chrome to use AR.",
  [XRCheckStatus.NO_TARGET_URL]: "Unable to load the AR experience.",
  [XRCheckStatus.DESKTOP]: "AR is available on mobile devices.",
  [XRCheckStatus.SHOW_APP_CLIP]: "Tap below to open in App Clip.",
};

// ============================================================================
// Compatibility check
// ============================================================================
interface UseXRCheckInlineReturn {
  status: XRCheckStatus;
  isChecking: boolean;
  isCompatible: boolean;
  statusMessage: string | null;
  showAppClipButton: boolean;
}

export function useXRCheckInline(): UseXRCheckInlineReturn {
  const [status, setStatus] = useState<XRCheckStatus>(XRCheckStatus.CHECKING);

  const isChecking = status === XRCheckStatus.CHECKING;
  const showAppClipButton = status === XRCheckStatus.SHOW_APP_CLIP;
  const isCompatible = status === XRCheckStatus.OK || status === XRCheckStatus.SHOW_APP_CLIP;
  
  const statusMessage = useMemo(() => {
    if (status in XR_STATUS_MESSAGES) {
      return XR_STATUS_MESSAGES[status];
    }
    return null;
  }, [status]);

  // Check on mount
  useEffect(() => {
    const checkXRSupport = async () => {
      try {
        // Check XR support
        let hasXR = false;
        if (navigator.xr && navigator.xr.isSessionSupported) {
          try {
            hasXR = await navigator.xr.isSessionSupported('immersive-ar');
          } catch {
            hasXR = false;
          }
        }

        // If XR is supported, we're good
        if (hasXR) {
          setStatus(XRCheckStatus.OK);
          return;
        }

        // Check incognito mode
        let isIncognito = false;
        let browserName: string | undefined;
        try {
          const result = await detectIncognito();
          isIncognito = result.isPrivate;
          browserName = result.browserName;
          
          // Check for specific iOS browsers
          if (navigator.userAgent.includes("CriOS")) {
            browserName = "Chrome iOS";
          }
          if (navigator.userAgent.includes("FxiOS")) {
            browserName = "Firefox iOS";
          }
        } catch {
          // Incognito detection failed, continue
        }

        // iOS checks
        if (isIOS()) {
          if (browserName && browserName !== "Safari") {
            setStatus(XRCheckStatus.IOS_NOT_SAFARI);
            return;
          }
          if (isIncognito) {
            setStatus(XRCheckStatus.IOS_INCOGNITO);
            return;
          }
          
          const iosVersion = getIOSVersion();
          const hasAppClipSupport = iosVersion !== null && iosVersion >= 15.6;
          
          if (!hasAppClipSupport) {
            setStatus(XRCheckStatus.IOS_NO_APP_CLIP);
            return;
          }

          // iOS without WebXR - show App Clip button
          setStatus(XRCheckStatus.SHOW_APP_CLIP);
          return;
        }

        // Android checks
        if (isMobileDevice()) {
          if (browserName && browserName !== "Chrome") {
            setStatus(XRCheckStatus.ANDROID_NOT_CHROME);
            return;
          }
          // Android Chrome without WebXR - might still work, try anyway
          setStatus(XRCheckStatus.OK);
          return;
        }

        // Desktop - not compatible
        setStatus(XRCheckStatus.DESKTOP);
      } catch (error) {
        console.error('[useXRCheckInline] Check failed:', error);
        setStatus(XRCheckStatus.OK); // Fall through to try anyway
      }
    };

    checkXRSupport();
  }, []);

  return {
    status,
    isChecking,
    isCompatible,
    statusMessage,
    showAppClipButton,
  };
}
